import { Box, Skeleton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Carousel from "../../components/Carousel";
import EmptySectionState from "../../components/EmptySectionState";
import PlaceholderFade from "../../components/PlaceholderFade";
import PosterTile from "../../components/PosterTile";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";
import { useRecentlyFinished } from "../../hooks/useCustomQuery";
import {
  getContentNoun,
  getContentOptions,
  getContentTypeSlug,
  getStatusOptions,
  getTrackPath,
} from "../../utils/utils";
import { SLOT_SX } from "./ProfileStandouts";

// The All view's split by type, in this fixed order
const TYPE_ORDER = ["movie", "tvshow", "book"];

// A month card's slot: as wide as the card's content, and stretched to the
// row's height by the flex row, so its right border runs the full height of
// the posters beside it
const MONTH_SLOT_SX = { borderRight: 1, borderColor: "divider" };

const shortMonth = (year, month) =>
  new Date(year, month - 1, 1).toLocaleString("en", { month: "short" });

/**
 * A month card's title: "Sep" (this year) / "Dec 2025" (other years), or for
 * the current month the days so far — "1–8 Oct", "1 Oct" on the 1st — so it
 * reads as a pair with the footnote's "vs 1–8 Sep". The year is left off this
 * year's months so every title fits a phone-width card on one line.
 */
function monthTitle(year, month) {
  const today = dayjs();
  const name = shortMonth(year, month);
  const suffix = year === today.year() ? "" : ` ${year}`;
  const isCurrent = year === today.year() && month === today.month() + 1;
  if (!isCurrent || today.date() === today.daysInMonth()) {
    return `${name}${suffix}`;
  }
  const days = today.date() === 1 ? "1" : `1–${today.date()}`;
  return `${days} ${name}${suffix}`;
}

/**
 * What a month is compared against, from the date the comparison span ends
 * (it always starts on the 1st): "vs Aug" for a whole month, or "vs 1–8 Sep"
 * for the current month, which is compared like-for-like only up to today's
 * day of the month.
 */
function comparisonPeriod(previousEnd) {
  const [year, month, day] = previousEnd.split("-").map(Number);
  const name = shortMonth(year, month);
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day === daysInMonth) return name;
  return day === 1 ? `1 ${name}` : `1–${day} ${name}`;
}

/**
 * A poster's caption: its finish date, then its rating if it has one — small
 * and muted rather than a star widget: "3 Oct · ★ 4.5", or "3 Oct". The date
 * leads so it sits in the same place on every poster. An undated poster shows
 * just its rating (or nothing). Tapping the poster opens the entry, where the
 * rating and date can be set or changed.
 */
function posterCaption({ rating, date }) {
  const stars = rating != null ? `★ ${rating}` : null;
  if (!date) return stars;
  const [year, month, day] = date.split("-").map(Number);
  const finished = `${day} ${shortMonth(year, month)}`;
  return stars ? `${finished} · ${stars}` : finished;
}

const arrow = (difference) => (difference > 0 ? "▲" : "▼");

/**
 * A count's change since the comparison period, set smaller and lighter than
 * the count it follows ("4 ▲2") — the usual dashboard convention, so it needs
 * no label of its own. A bar in the arrow's slot when unchanged ("4 ▬"), so the
 * row reads as "same as before" rather than as missing its change.
 */
function Change({ count, previous }) {
  const difference = count - previous;
  return (
    <Box component="span" sx={{ fontSize: "0.85em", opacity: 0.75 }}>
      {difference === 0 ? (
        // An invisible ▲ with a bar drawn over it, so the slot matches the
        // arrow's width in whatever font renders it; the bar is inset to drop
        // the glyph's side spacing
        <Box
          component="span"
          sx={{
            position: "relative",
            color: "transparent",
            "&::after": {
              content: '""',
              position: "absolute",
              left: "20%",
              right: "20%",
              top: "50%",
              height: "0.3em",
              transform: "translateY(-50%)",
              bgcolor: "text.secondary",
            },
          }}
        >
          ▲
        </Box>
      ) : (
        <>
          {arrow(difference)}
          {Math.abs(difference)}
        </>
      )}
    </Box>
  );
}

/**
 * Opens each month in the row: a right-aligned block, only as wide as its
 * content (its slot draws the divider line beside the month's posters — see
 * MONTH_SLOT_SX), with the month and "{n} finished", then —
 *   All: a stack of icon + count per type (zeros left out), each with its
 *     change since last month, and one footnote naming the comparison period
 *     ("vs 1–8 Sep") rather than a label on every row;
 *   a single type: "▲ 3 vs 1–8 Sep" / "Same as Aug".
 * Muted throughout, with no red/green — the comparison is context, not a score.
 */
function MonthCard({ stats, contentType }) {
  const contentOptions = getContentOptions();
  const period = comparisonPeriod(stats.previous_end);
  const types = TYPE_ORDER.filter((type) => stats.by_type[type] > 0);
  const difference = stats.count - stats.previous;

  return (
    <Box
      sx={{
        pl: 0.5,
        pr: 1.25,
        py: 1.25,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        textAlign: "right",
        gap: 0.25,
        whiteSpace: "nowrap",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        lineHeight={1.2}
        mb={0.25}
      >
        {monthTitle(stats.year, stats.month)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {stats.count.toLocaleString()} finished
      </Typography>

      {contentType === "all" ? (
        <Stack
          alignItems="flex-end"
          spacing={0.25}
          sx={{ color: "text.secondary" }}
        >
          {/* A grid (icon | count | change) rather than right-aligned rows, so
              icons and counts stay in columns however wide each change is */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto auto auto",
              alignItems: "center",
              columnGap: 0.75,
              rowGap: 0.25,
              "& svg": { fontSize: "0.95rem" },
            }}
          >
            {types.map((type) => (
              <Box
                key={type}
                aria-label={`${stats.by_type[type]} ${contentOptions[type].labelPlural}`}
                sx={{ display: "contents" }}
              >
                {contentOptions[type].icon}
                <Typography variant="caption">
                  {stats.by_type[type].toLocaleString()}
                </Typography>
                <Typography variant="caption" textAlign="left">
                  <Change
                    count={stats.by_type[type]}
                    previous={stats.previous_by_type[type]}
                  />
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Every row shows a change or a dash, so the footnote always applies */}
          {types.length > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              vs {period}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {difference === 0
            ? `Same as ${period}`
            : `${arrow(difference)} ${Math.abs(difference)} vs ${period}`}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Opens the undated finishes at the end of the row, in place of a month card:
 * says why they have no counts, as a nudge to add dates. Unlike a month card
 * it wraps, at about a poster's width, so the prompt doesn't widen the slot.
 */
function UndatedCard() {
  return (
    <Box
      sx={{
        pl: 0.5,
        pr: 1.25,
        py: 1.25,
        width: 110,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        textAlign: "right",
        gap: 0.25,
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        lineHeight={1.2}
        mb={0.25}
      >
        Undated
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Add finish dates to group these by month
      </Typography>
    </Box>
  );
}

/**
 * Dashboard "Recently finished": your latest finishes (up to 20, by finish
 * date), each month opened by a MonthCard with that month's counts. When there
 * are fewer than 20 dated finishes, undated ones fill the rest of the row
 * behind an UndatedCard. "See all"
 * opens the same content type's finished tab on Track, sorted by finish date
 * and grouped by month (grouping is Track's default for date sorts).
 */
export default function DashboardRecentlyFinished({ username, contentType }) {
  const { data, isLoading, isPlaceholderData } = useRecentlyFinished(
    username,
    contentType,
  );
  const { openContent } = useDetailNavigation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
    );
  }
  if (!data) return null;

  // Interleave a divider card before each month's first item, and before the
  // undated items (which the backend sends last)
  const statsByMonth = new Map(
    data.months.map((stats) => [`${stats.year}-${stats.month}`, stats]),
  );
  const rowItems = [];
  let currentGroup = null;
  data.items.forEach((item) => {
    const [year, month] = item.date?.split("-").map(Number) ?? [];
    const key = item.date ? `${year}-${month}` : "undated";
    if (key !== currentGroup) {
      currentGroup = key;
      rowItems.push(
        item.date
          ? { monthCard: statsByMonth.get(key), key: `month-${key}` }
          : { undatedCard: true, key: "undated" },
      );
    }
    rowItems.push({ ...item, key: `entry-${item.journal_id}` });
  });
  const isDivider = (item) => item.monthCard || item.undatedCard;

  return (
    <PlaceholderFade active={isPlaceholderData}>
      <Carousel
        title="Recently finished"
        titleIcon={getStatusOptions().finished.iconFilled}
        items={rowItems}
        getItemKey={(item) => item.key}
        itemSx={(item) => (isDivider(item) ? MONTH_SLOT_SX : SLOT_SX)}
        // Divider cards aren't interactive; posters have no long press, so the
        // full hover + press-in
        itemScale={(item) => (isDivider(item) ? "none" : "full")}
        renderItem={(item) =>
          item.undatedCard ? (
            <UndatedCard />
          ) : item.monthCard ? (
            <MonthCard stats={item.monthCard} contentType={contentType} />
          ) : (
            <PosterTile
              contentId={item.content_id}
              contentType={item.content_type}
              contentTitle={item.content_title}
              imageUrl={item.image_url}
              overlay={true}
              // The All view mixes types, so mark each poster's type
              showType={contentType === "all"}
              onClick={() =>
                openContent({
                  contentId: item.content_id,
                  contentType: item.content_type,
                  title: item.content_title,
                  entryId: item.journal_id,
                })
              }
            />
          )
        }
        // Divider cards have no caption (their slot keeps the line, so posters
        // stay aligned)
        renderCaption={(item) => (isDivider(item) ? null : posterCaption(item))}
        // Turns month grouping on, to match this row, and it stays on as the
        // remembered choice (see groupByMonth.js); none
        // when empty, as there's nothing to see
        seeMoreLink={
          data.items.length === 0
            ? undefined
            : `${getTrackPath(username, contentType, "finished")}?sortBy=finish_date&groupByMonth=true`
        }
        // Points new users at Explore, filtered to this type
        emptyState={
          <EmptySectionState
            // "Mark movies finished…" on a type's tab, "things" on All
            message={`Mark ${getContentNoun({ type: contentType, plural: true })} finished to see monthly summaries here`}
            actionLabel="Explore"
            onAction={() =>
              navigate(
                contentType === "all"
                  ? "/explore"
                  : `/explore/${getContentTypeSlug(contentType)}`,
              )
            }
          />
        }
      />
    </PlaceholderFade>
  );
}
